export const ANALYTICAL_TASK_SCHEMA_VERSION = 2 as const

export type AnalyticalTaskState = 'not_started' | 'in_progress' | 'in_review' | 'done'
export type AnalyticalSubjectKind = 'product' | 'character' | 'rhetorical-device' | 'section' | 'custom'
export type CharacterIndexWitnessRole = 'primary' | 'independent' | 'comparative'
export type AnalyticalReadinessState = 'ready' | 'missing' | 'blocked' | 'skipped' | 'not_applicable'
export type AnalyticalCollationPolicy = 'required' | 'optional' | 'skipped'

export type BuiltInAnalyticalDimension =
  | 'cast'
  | 'identity'
  | 'coreference'
  | 'characterization'
  | 'focalization'
  | 'speaker'
  | 'sections'
  | 'occurrences'
  | 'classification'
  | 'participants'
  | 'interpretation'
  | 'review'

export type AnalyticalDimension = BuiltInAnalyticalDimension | `custom:${string}`

export interface AnalyticalPosition {
  rowId: string
  unitLabel?: string
  localOffset?: number
}
export interface CharacterIndexTaskSetEnrollment {
  /** Canonical authority-work identity. Legacy/Reader-created plans may use the held work id. */
  workId: string
  workLabel: string
  /** Canonical authority-section identity. Legacy/Reader-created plans may use the held chapter id. */
  chapterId: string
  chapterLabel?: string
  witnessId: string
  witnessLabel: string
  sourceKind?: 'held' | 'catalogued'
  sourceRecordId?: string
  /** Operational ids route canonical scope back into the Reader when held text exists. */
  operationalWorkId?: string
  operationalChapterId?: string
  role: CharacterIndexWitnessRole
  readiness: {
    sourceObject: AnalyticalReadinessState
    transcription: AnalyticalReadinessState
    collation: AnalyticalReadinessState
  }
  collationPolicy: AnalyticalCollationPolicy
}

export interface CharacterIndexSectionScope {
  sectionId: string
  sectionLabel: string
}

export interface CharacterIndexWorkScope {
  workId: string
  workLabel: string
  sections: CharacterIndexSectionScope[]
}

export interface CharacterIndexPlanScope {
  works: CharacterIndexWorkScope[]
}

export interface AnalyticalSubject {
  id: string
  kind: AnalyticalSubjectKind
  externalId: string
  label: string
  metadata?: Record<string, unknown>
  createdAt: string
  updatedAt: string
  archivedAt?: string
  mergedIntoSubjectId?: string
}

export interface AnalyticalWorkstream {
  id: string
  subjectId: string
  dimension: AnalyticalDimension
  state: AnalyticalTaskState
  chapterId: string
  witnessId: string
  order: number
  createdAt: string
  updatedAt: string
  startedAt?: string
  reviewAt?: string
  completedAt?: string
  lastActivityAt?: string
  lastPosition?: AnalyticalPosition
  archivedAt?: string
  mergedIntoWorkstreamId?: string
}

export interface AnalyticalCheckpointScope {
  chapterId: string
  witnessId: string
  start: AnalyticalPosition
  end?: AnalyticalPosition
  resume: AnalyticalPosition
}

export interface AnalyticalCheckpoint {
  id: string
  title?: string
  state: AnalyticalTaskState
  workstreamIds: string[]
  subjectIds: string[]
  dimensions: AnalyticalDimension[]
  scope: AnalyticalCheckpointScope
  createdAt: string
  updatedAt: string
  reviewAt?: string
  completedAt?: string
  notes?: string
}

export interface AnalyticalTaskSet {
  id: string
  schemaVersion: typeof ANALYTICAL_TASK_SCHEMA_VERSION
  productKind: 'character-index'
  title: string
  state: AnalyticalTaskState
  /** Editorially chosen canonical scope, distinct from operational witness enrollments. */
  scope: CharacterIndexPlanScope
  enrollments: CharacterIndexTaskSetEnrollment[]
  /** Denormalized scopes make product plans queryable without opening every record. */
  workIds: string[]
  chapterIds: string[]
  witnessIds: string[]
  subjects: AnalyticalSubject[]
  workstreams: AnalyticalWorkstream[]
  checkpoints: AnalyticalCheckpoint[]
  createdAt: string
  updatedAt: string
  reviewAt?: string
  completedAt?: string
  metadata?: Record<string, unknown>
}
